import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

import PredictorTable from './PredictorTable';
import PredictorCards from './PredictorCards';
import CreatePredictor from './CreatePredictor';
import EditPredictor from './EditPredictor';
import Pagination from './Pagination';
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { toast } from "../shared/ToastProvider";
import EmptyState from "../shared/EmptyState";
import DeleteConfirmation from "../shared/DeleteConfirmation";

const Predictor = () => {
    const [items, setItems] = useState([]);
    const [newPredictor, setNewPredictor] = useState({ predictorname: '', proficiency_score: '' });
    const [editPredictor, setEditPredictor] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [totalItems, setTotalItems] = useState(0);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await api.get('/predictors', {
                    params: { limit: rowsPerPage, offset: (currentPage - 1) * rowsPerPage }
                });
                setItems(response.data.data);
                setTotalItems(response.data.total);
            } catch (error) {
                console.error('Erreur lors de la recuperation des donnees', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentPage, rowsPerPage]);

    const handleCreate = async () => {
        try {
            const response = await api.post('/predictors', newPredictor);
            setItems((prev) => [...prev, response.data]);
            setTotalItems((prev) => prev + 1);
            setNewPredictor({ predictorname: '', proficiency_score: '' });
            toast.success('Prédicteur ajouté avec succès');
        } catch (error) {
            toast.error("Erreur lors de l'ajout");
            console.error('Erreur lors de la création du prédicteur :', error);
        }
    };

    const handleDeleteRequest = (predictor_id) => {
        setDeleteTarget(predictor_id);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        setIsDeleteDialogOpen(false);
        if (!deleteTarget) return;
        try {
            const response = await api.delete(`/predictors/${deleteTarget}`);
            if (response.status === 200) {
                setItems((prev) => prev.filter((item) => item.predictor_id !== deleteTarget));
                setTotalItems((prev) => prev - 1);
                toast.success('Prédicteur supprimé avec succès');
            }
        } catch (error) {
            toast.error('Erreur lors de la suppression');
            console.error('Erreur lors de la suppression du prédicteur :', error);
        } finally {
            setDeleteTarget(null);
        }
    };

    const handleUpdate = async () => {
        if (!editPredictor) return;
        try {
            const response = await api.put(`/predictors/${editPredictor.predictor_id}`, editPredictor);
            setItems((prev) =>
                prev.map((item) =>
                    item.predictor_id === editPredictor.predictor_id ? response.data : item
                )
            );
            setEditPredictor(null);
            toast.success('Prédicteur mis à jour avec succès');
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
            console.error('Erreur lors de la mise à jour du prédicteur :', error);
        }
    };

    const seeCommentsOfAPredictor = (predictor_id) => {
        navigate(`/comment?predictor_id=${predictor_id}`);
    }

    const seePredictionsOfAPredictor = (predictorId) => {
        navigate(`/prediction/?predictor_id=${predictorId}`);
    };

    const currentItems = items;

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalItems / rowsPerPage);
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <Container className="py-6 md:py-10">
            <PageHeader title="Prédicteurs" />

            <CreatePredictor
                newPredictor={newPredictor}
                setNewPredictor={setNewPredictor}
                handleCreate={handleCreate}
            />

            {items.length === 0 && !loading ? (
                <EmptyState
                    title="Aucun prédicteur trouvé"
                    subtitle="Ajoutez votre premier prédicteur pour commencer."
                    actionLabel="Ajouter"
                    onAction={() => document.getElementById('create-predictor-name')?.focus()}
                />
            ) : (
                <>
                    {editPredictor && (
                        <EditPredictor
                            editPredictor={editPredictor}
                            setEditPredictor={setEditPredictor}
                            handleUpdate={handleUpdate}
                        />
                    )}

                    <div className="flex justify-end mb-4">
                        <select
                            className="border border-input bg-background text-foreground p-2 rounded"
                            value={rowsPerPage}
                            onChange={e => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setCurrentPage(1);
                            }}
                        >
                            {[5, 10, 15, 20, 50].map(n => <option key={n} value={n}>{n} par page</option>)}
                        </select>
                    </div>

                    <div className="hidden md:block">
                        <PredictorTable
                            currentItems={currentItems}
                            seeCommentsOfAPredictor={seeCommentsOfAPredictor}
                            seePredictionsOfAPredictor={seePredictionsOfAPredictor}
                            setEditPredictor={setEditPredictor}
                            handleDelete={handleDeleteRequest}
                        />
                    </div>
                    <div className="md:hidden">
                        <PredictorCards
                            items={currentItems}
                            loading={loading}
                            onEdit={(item) => setEditPredictor(item)}
                            onDeleteRequest={(id) => handleDeleteRequest(id)}
                            seeCommentsOfAPredictor={seeCommentsOfAPredictor}
                            seePredictionsOfAPredictor={seePredictionsOfAPredictor}
                        />
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        rowsPerPage={rowsPerPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        handlePageClick={handlePageClick}
                    />
                </>
            )}

            <DeleteConfirmation
                isOpen={isDeleteDialogOpen}
                onClose={() => { setIsDeleteDialogOpen(false); setDeleteTarget(null); }}
                onConfirm={handleDeleteConfirmed}
                title="Confirmer la suppression"
                description="Voulez-vous vraiment supprimer ce prédicteur ? Cette action est irréversible."
                confirmText="Supprimer"
            />
        </Container>
    );
};

export default Predictor;
